import React from 'react';
import { Box, IconButton, Slider, Tooltip, Typography } from '@mui/material';

import { useTranslation } from 'react-i18next';
import { FEEL_PROFILES, type FeelLevel } from './feelProfiles';
import NumberField from '../common/NumberField';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../utils/iconFallbacks';
import { opsColors } from '../../theme/opsTokens';

interface ServiceFeelStepProps {
  feelLevel: FeelLevel;
  feelSlope: number;
  feelInterceptMinutes: number;
  maxTripTimeMinutes: number;
  onFeelLevelChange: (value: FeelLevel) => void;
  onMaxTripTimeChange: (value: number) => void;
}

const FEEL_LEVELS: FeelLevel[] = [1, 2, 3, 4, 5];
const TOOLTIP_POPPER_SX = { zIndex: 2605 } as const;
const FEEL_ICONS: Record<FeelLevel, string> = {
  1: ICON_FALLBACKS.LocalTaxiOutlined,
  2: ICON_FALLBACKS.Autorenew,
  3: ICON_FALLBACKS.Balance,
  4: ICON_FALLBACKS.Route,
  5: ICON_FALLBACKS.DirectionsBusFilledOutlined,
};

const DIAGRAM = {
  width: 680,
  height: 232,
  left: 56,
  right: 24,
  top: 18,
  bottom: 46,
};

export default function ServiceFeelStep({
  feelLevel,
  feelSlope,
  feelInterceptMinutes,
  maxTripTimeMinutes,
  onFeelLevelChange,
  onMaxTripTimeChange,
}: ServiceFeelStepProps) {
  const { t } = useTranslation('onboarding');
  const feelLabels: Record<FeelLevel, string> = {
    1: t('serviceFeel.profileNames.1', { defaultValue: FEEL_PROFILES[1].name }),
    2: t('serviceFeel.profileNames.2', { defaultValue: FEEL_PROFILES[2].name }),
    3: t('serviceFeel.profileNames.3', { defaultValue: FEEL_PROFILES[3].name }),
    4: t('serviceFeel.profileNames.4', { defaultValue: FEEL_PROFILES[4].name }),
    5: t('serviceFeel.profileNames.5', { defaultValue: FEEL_PROFILES[5].name }),
  };
  const feelMarks = [
    {
      value: 1,
      label: (
        <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
          <Typography variant="caption" sx={{ color: FEEL_PROFILES[1].color }}>
            {t('serviceFeel.feelMarks.taxi')}
          </Typography>
          <Icon size={28} d={ICON_FALLBACKS.LocalTaxiOutlined} style={{ color: FEEL_PROFILES[1].color }} />
        </Box>
      ),
    },
    {
      value: 2,
      label: (
        <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
          <Typography variant="caption" sx={{ color: FEEL_PROFILES[2].color }}>
            {t('serviceFeel.feelMarks.responsive')}
          </Typography>
          <Icon size={28} name="arrows-rotate" style={{ color: FEEL_PROFILES[2].color }} />
        </Box>
      ),
    },
    {
      value: 3,
      label: (
        <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
          <Typography variant="caption" sx={{ color: FEEL_PROFILES[3].color }}>
            {t('serviceFeel.feelMarks.balanced')}
          </Typography>
          <Icon size={28} d={ICON_FALLBACKS.Balance} style={{ color: FEEL_PROFILES[3].color }} />
        </Box>
      ),
    },
    {
      value: 4,
      label: (
        <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
          <Typography variant="caption" sx={{ color: FEEL_PROFILES[4].color }}>
            {t('serviceFeel.feelMarks.efficient')}
          </Typography>
          <Icon size={28} name="route" style={{ color: FEEL_PROFILES[4].color }} />
        </Box>
      ),
    },
    {
      value: 5,
      label: (
        <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
          <Typography variant="caption" sx={{ color: FEEL_PROFILES[5].color }}>
            {t('serviceFeel.feelMarks.bus')}
          </Typography>
          <Icon size={28} name="bus" style={{ color: FEEL_PROFILES[5].color }} />
        </Box>
      ),
    },
  ];
  const [exampleDirectMinutes, setExampleDirectMinutes] = React.useState<number>(10);
  const activeLevel = feelLevel;
  const activeProfile = FEEL_PROFILES[activeLevel];
  const yMax = Math.max(10, Math.ceil(maxTripTimeMinutes / 10) * 10);
  const yTickStep = Math.max(10, Math.ceil(yMax / 4 / 10) * 10);
  const yTicks = Array.from({ length: Math.floor(yMax / yTickStep) + 1 }, (_, i) => i * yTickStep);
  const chartHeight = DIAGRAM.height - DIAGRAM.top - DIAGRAM.bottom;
  const yToPx = (y: number) => DIAGRAM.top + (1 - y / yMax) * chartHeight;
  const baselineY = yToPx(0);
  const riderX = DIAGRAM.left + 152;
  const peakCenterX = DIAGRAM.left + 332;
  const destinationX = DIAGRAM.width - DIAGRAM.right - 28;
  const busX = DIAGRAM.left + 26;
  const sampleDirectMinutes = exampleDirectMinutes;
  const peakGapHalfWidth = 18;
  const profileExamples = FEEL_LEVELS.map((level) => {
    const profile = FEEL_PROFILES[level];
    const maxOnboardMinutes = Math.min(profile.interceptMinutes + profile.slope * sampleDirectMinutes, maxTripTimeMinutes);
    const peakY = yToPx(maxOnboardMinutes);

    return {
      level,
      peakX: peakCenterX,
      peakY,
      maxOnboardMinutes,
      isActive: level === activeLevel,
    };
  });
  const activeExample = profileExamples.find((profile) => profile.level === activeLevel) ?? profileExamples[2];
  const maxOnboardLabel = t('serviceFeel.maxOnboardLabel', { value: Math.round(activeExample.maxOnboardMinutes) });
  const maxOnboardPillWidth = Math.max(92, Math.round(maxOnboardLabel.length * 6.4 + 22));
  const maxOnboardPillX = Math.max(DIAGRAM.left + 2, riderX - maxOnboardPillWidth - 8);
  const maxOnboardPillY = activeExample.peakY - 12;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 1.5 }}>
        {t('serviceFeel.title')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t('serviceFeel.description')}
      </Typography>

      <Box
        sx={(theme) => ({
          border: `1px solid ${opsColors.neutral.line}`,
          borderRadius: 2,
          backgroundColor: opsColors.neutral.panel,
          p: 2.5,
        })}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            mb: 1.5,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
            {t('serviceFeel.maxTripTime')}
          </Typography>
          <NumberField
            value={maxTripTimeMinutes}
            onChange={(v) => onMaxTripTimeChange(v ?? 1)}
            allowEmpty={false}
            fallback={1}
            min={1}
            step={1}
            integer
            size="small"
            sx={{ width: 180 }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', ml: { xs: 0, sm: 2 } }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t('serviceFeel.currentProfile', { name: feelLabels[feelLevel] })}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25, mt: 0.15 }}>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color: 'text.secondary' }}>
                <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
                  {t('serviceFeel.slope', { value: feelSlope.toFixed(1) })}
                </Typography>
                <Tooltip
                  title={t('serviceFeel.slopeTooltip')}
                  arrow
                  slotProps={{ popper: { sx: TOOLTIP_POPPER_SX } }}
                >
                  <IconButton
                    size="small"
                    aria-label={t('serviceFeel.slopeAriaLabel')}
                    sx={{ p: 0.25, color: 'text.secondary' }}
                  >
                    <Icon size={16} name="circle-info" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color: 'text.secondary' }}>
                <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
                  {t('serviceFeel.intercept', { value: feelInterceptMinutes })}
                </Typography>
                <Tooltip
                  title={t('serviceFeel.interceptTooltip')}
                  arrow
                  slotProps={{ popper: { sx: TOOLTIP_POPPER_SX } }}
                >
                  <IconButton
                    size="small"
                    aria-label={t('serviceFeel.interceptAriaLabel')}
                    sx={{ p: 0.25, color: 'text.secondary' }}
                  >
                    <Icon size={16} name="circle-info" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </Box>
        <Slider
          value={feelLevel}
          min={1}
          max={5}
          step={1}
          marks={feelMarks}
          valueLabelDisplay="auto"
          data-testid="wizard.service-feel.slider"
          onChange={(_, value) => onFeelLevelChange((Number(value) as 1 | 2 | 3 | 4 | 5) ?? 3)}
          sx={{
            mb: 5,
            color: activeProfile.color,
            height: 10,
            '& .MuiSlider-track': {
              border: 'none',
              backgroundColor: activeProfile.color,
              height: 10,
            },
            '& .MuiSlider-rail': {
              backgroundColor: '#D2D6DC',
              opacity: 1,
              height: 10,
            },
            '& .MuiSlider-thumb': {
              backgroundColor: activeProfile.color,
              width: 28,
              height: 28,
              border: '3px solid #fff',
              boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            },
            '& .MuiSlider-markLabel': {
              mt: 1,
              fontWeight: 600,
              fontSize: '0.9rem',
            },
            '& .MuiSlider-mark': {
              height: 10,
              width: 2,
            },
          }}
        />
        <Box
          sx={(theme) => ({
            mt: 1.25,
            border: `1px solid ${opsColors.neutral.line}`,
            borderRadius: 1.5,
            p: 1,
            backgroundColor: opsColors.neutral.panelAlt,
          })}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: 1.1,
              mb: 0.85,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                color: 'text.primary',
                fontWeight: 700,
                textAlign: 'center',
                lineHeight: 1.2,
              }}
            >
              {t('serviceFeel.exampleHeading')}
            </Typography>
            <NumberField
              value={exampleDirectMinutes}
              onChange={(v) => {
                if (v !== undefined && Number.isFinite(v) && v >= 1 && v <= 180) {
                  setExampleDirectMinutes(v);
                }
              }}
              allowEmpty={false}
              fallback={1}
              min={1}
              max={180}
              step={1}
              integer
              size="small"
              sx={{
                width: 92,
                '& .MuiInputBase-root': {
                  fontWeight: 700,
                },
                '& input': {
                  textAlign: 'center',
                  fontWeight: 700,
                },
              }}
            />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {t('serviceFeel.exampleMinutesSuffix')}
            </Typography>
          </Box>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <Box sx={{ minWidth: 520 }}>
              <svg
                width="100%"
                viewBox={`0 0 ${DIAGRAM.width} ${DIAGRAM.height}`}
                role="img"
                aria-label={t('serviceFeel.diagramAriaLabel')}
              >
                <title>{t('serviceFeel.diagramTitle')}</title>
                <defs>
                  <marker id="journey-arrow-active" markerWidth="7" markerHeight="7" refX="6.5" refY="3.5" orient="auto">
                    <polygon points="0 0, 7 3.5, 0 7" fill={activeProfile.color} />
                  </marker>
                </defs>
                {yTicks.map((tick) => (
                  <g key={`y-${tick}`}>
                    <line
                      x1={DIAGRAM.left}
                      y1={yToPx(tick)}
                      x2={DIAGRAM.width - DIAGRAM.right}
                      y2={yToPx(tick)}
                      stroke="rgba(0,0,0,0.1)"
                    />
                    <text x={DIAGRAM.left - 8} y={yToPx(tick) + 4} textAnchor="end" fontSize="11" fill="#666">
                      {tick}
                    </text>
                  </g>
                ))}
                <line
                  x1={DIAGRAM.left}
                  y1={DIAGRAM.top}
                  x2={DIAGRAM.left}
                  y2={DIAGRAM.height - DIAGRAM.bottom}
                  stroke="#444"
                />
                <line
                  x1={DIAGRAM.left}
                  y1={baselineY}
                  x2={DIAGRAM.width - DIAGRAM.right}
                  y2={baselineY}
                  stroke="#444"
                  strokeOpacity={0.35}
                />
                <line
                  x1={riderX}
                  y1={baselineY}
                  x2={destinationX}
                  y2={baselineY}
                  stroke="#7B7E85"
                  strokeWidth={2.4}
                  strokeDasharray="12 10"
                />
                <path
                  d={`M ${busX} ${baselineY} L ${riderX - 22} ${baselineY}`}
                  fill="none"
                  stroke={activeProfile.color}
                  strokeWidth={1.8}
                  markerEnd="url(#journey-arrow-active)"
                />
                {profileExamples.filter((profile) => !profile.isActive).map((profile) => (
                  <g key={`profile-journey-${profile.level}`}>
                    <polyline
                      points={`${riderX},${baselineY} ${riderX},${profile.peakY} ${profile.peakX - peakGapHalfWidth},${profile.peakY}`}
                      fill="none"
                      stroke="#A9AFBA"
                      strokeWidth={1.4}
                      strokeOpacity={0.85}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <polyline
                      points={`${profile.peakX + peakGapHalfWidth},${profile.peakY} ${destinationX},${profile.peakY} ${destinationX},${baselineY}`}
                      fill="none"
                      stroke="#A9AFBA"
                      strokeWidth={1.4}
                      strokeOpacity={0.85}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle cx={profile.peakX} cy={profile.peakY} r={9.5} fill="#fff" stroke="#A9AFBA" strokeWidth={1.5} />
                    <foreignObject x={profile.peakX - 8} y={profile.peakY - 8} width={16} height={16}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16 }}>
                        <Icon size={14} d={FEEL_ICONS[profile.level]} style={{ color: '#8A909B' }} />
                      </Box>
                    </foreignObject>
                  </g>
                ))}
                <path
                  d={`M ${riderX} ${baselineY} L ${riderX} ${activeExample.peakY} L ${activeExample.peakX - peakGapHalfWidth} ${activeExample.peakY}`}
                  fill="none"
                  stroke={activeProfile.color}
                  strokeWidth={2.3}
                  markerEnd="url(#journey-arrow-active)"
                />
                <path
                  d={`M ${activeExample.peakX + peakGapHalfWidth} ${activeExample.peakY} L ${destinationX} ${activeExample.peakY} L ${destinationX} ${baselineY - 22}`}
                  fill="none"
                  stroke={activeProfile.color}
                  strokeWidth={2.3}
                  markerEnd="url(#journey-arrow-active)"
                />
                <circle cx={busX} cy={baselineY} r={12} fill="#FFFFFF" stroke="#202124" strokeWidth={2} />
                <rect x={busX - 6.2} y={baselineY - 5.8} width={12.4} height={9.6} rx={1.5} fill="#202124" />
                <rect x={busX - 4.8} y={baselineY - 4.2} width={9.6} height={4} rx={0.8} fill="#FFFFFF" />
                <circle cx={busX - 3.8} cy={baselineY + 4.4} r={1.5} fill="#202124" />
                <circle cx={busX + 3.8} cy={baselineY + 4.4} r={1.5} fill="#202124" />
                <g transform={`translate(${riderX - 10.8}, ${baselineY - 14}) scale(0.9)`} fill="#202124">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4z" />
                  <path d="M12 14c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </g>
                <circle cx={activeExample.peakX} cy={activeExample.peakY} r={10.5} fill="#fff" stroke={activeProfile.color} strokeWidth={2} />
                <foreignObject x={activeExample.peakX - 9} y={activeExample.peakY - 9} width={18} height={18}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18 }}>
                    <Icon size={16} d={FEEL_ICONS[activeLevel]} style={{ color: activeProfile.color }} />
                  </Box>
                </foreignObject>
                <circle cx={destinationX} cy={baselineY} r={10} fill="#202124" />
                <circle cx={destinationX} cy={baselineY} r={3.4} fill="#fff" />
                <text x={busX} y={baselineY + 32} textAnchor="middle" fontSize="11" fill="#444">
                  {t('serviceFeel.vehicle')}
                </text>
                <text x={riderX} y={baselineY + 32} textAnchor="middle" fontSize="11" fill="#444">
                  {t('serviceFeel.pickup')}
                </text>
                <text x={destinationX} y={baselineY + 32} textAnchor="middle" fontSize="11" fill="#444">
                  {t('serviceFeel.destination')}
                </text>
                <g>
                  <rect
                    x={maxOnboardPillX}
                    y={maxOnboardPillY}
                    width={maxOnboardPillWidth}
                    height={22}
                    rx={11}
                    fill={activeProfile.color}
                    fillOpacity={0.14}
                    stroke={activeProfile.color}
                    strokeWidth={1.1}
                  />
                  <text
                    x={maxOnboardPillX + maxOnboardPillWidth / 2}
                    y={maxOnboardPillY + 15}
                    textAnchor="middle"
                    fontSize="11"
                    fill={activeProfile.color}
                    fontWeight={700}
                  >
                    {maxOnboardLabel}
                  </text>
                </g>
                <text
                  x={peakCenterX}
                  y={baselineY + 16}
                  textAnchor="middle"
                  fontSize="11"
                  fill="#6C727F"
                >
                  {t('serviceFeel.directTripBaseline')}
                </text>
                <text
                  x={14}
                  y={(DIAGRAM.top + DIAGRAM.height - DIAGRAM.bottom) / 2}
                  textAnchor="middle"
                  fontSize="11"
                  fill="#555"
                  transform={`rotate(-90 14 ${(DIAGRAM.top + DIAGRAM.height - DIAGRAM.bottom) / 2})`}
                >
                  {t('serviceFeel.maxOnboardAxisLabel')}
                </text>
              </svg>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
